import { ArrowLeft } from "lucide-react";

const Section = ({ num, title, children }) => (
  <div className="mb-10">
    <h2 className="font-outfit font-black text-white text-lg mb-4 pb-3 border-b border-white/8">
      <span className="text-[#3B9EFF] mr-2">{num}.</span>{title}
    </h2>
    <div className="space-y-3 text-[#A1A1AA] text-sm font-inter leading-relaxed">{children}</div>
  </div>
);

const Sub = ({ children }) => (
  <h3 className="text-white font-semibold text-sm pt-2">{children}</h3>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1.5">
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);

const InfoBox = ({ children }) => (
  <div className="bg-white/[0.03] border border-white/8 rounded-xl p-5 space-y-2">{children}</div>
);

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#0C0E13]">
      {/* Header */}
      <div className="border-b border-white/5 bg-[#0A0C10]">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm font-medium group">
            <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
            Back to Fanible
          </a>
          <div className="flex items-center gap-2">
            <img src="/fanible-icon.png" alt="Fanible" className="w-6 h-6 rounded-lg" style={{ mixBlendMode: "screen" }} />
            <span className="font-outfit font-black text-white text-sm uppercase tracking-tight">Fanible</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 md:px-10 py-14">

        {/* Title */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 bg-[#3B9EFF]/10 border border-[#3B9EFF]/25 rounded-full px-3 py-1.5 mb-5">
            <span className="text-[9px] font-black uppercase tracking-[0.2em] text-[#3B9EFF]">Legal</span>
          </div>
          <h1 className="font-outfit font-black text-white text-4xl sm:text-5xl tracking-tight mb-4">Terms &amp; Conditions</h1>
          <p className="text-[#A1A1AA] text-sm font-inter">Last Updated: August 27, 2026</p>
        </div>

        {/* Intro */}
        <div className="mb-10 p-6 bg-white/[0.03] border border-white/8 rounded-2xl space-y-3 text-[#A1A1AA] text-sm font-inter leading-relaxed">
          <p className="text-white font-semibold text-base">Welcome to Fanible!</p>
          <p>
            These Terms and Conditions ("Terms") govern your access to and use of the Fanible mobile
            application, website, products, services, and related features (collectively, the "Platform")
            operated by Fanible Private Limited, a company registered in Samastipur, Bihar, India
            ("Fanible", "we", "our", or "us").
          </p>
          <p>
            By creating an account, accessing, or using the Platform, you acknowledge that you have read,
            understood, and agree to be legally bound by these Terms. If you do not agree to these Terms,
            you must discontinue use of the Platform immediately.
          </p>
        </div>

        <Section num="1" title="About Fanible">
          <p>Fanible is a sports-focused social platform where users can:</p>
          <Bullets items={[
            "Participate in sports discussions",
            "Publish sports-related posts",
            "Share sports news, blogs, and articles",
            "Upload sports-related images",
            "Follow creators",
            "Vote and interact with content",
            "Comment on posts",
            "Purchase sports merchandise through the Fanible e-shop",
            "Support eligible creators through paid Shoutouts",
            "Earn money as eligible creators through received Shoutouts",
          ]} />
          <p className="text-white font-semibold">Fanible is intended solely for sports-related content and interactions.</p>
        </Section>

        <Section num="2" title="Eligibility">
          <p>You must comply with all applicable laws while using the Platform.</p>
          <p>By creating an account, you represent that:</p>
          <Bullets items={[
            "The information you provide is accurate",
            "You are legally permitted to use the Platform under applicable laws",
            "You will comply with these Terms",
          ]} />
          <p>
            Certain Platform features, including financial transactions, withdrawals, and creator
            monetization, may only be available to users who satisfy applicable legal and verification
            requirements.
          </p>
        </Section>

        <Section num="3" title="User Accounts">
          <p>You may register using:</p>
          <Bullets items={["Google Sign-In", "WhatsApp One-Time Password (OTP)", "Username and password"]} />
          <p>You are solely responsible for maintaining the confidentiality of your account credentials. You agree to:</p>
          <Bullets items={[
            "Keep your login information secure",
            "Promptly notify Fanible of unauthorized access",
            "Accept responsibility for all activities occurring under your account",
          ]} />
          <p>Fanible reserves the right to suspend or terminate accounts that provide false information or violate these Terms.</p>
        </Section>

        <Section num="4" title="Sports-Only Platform">
          <p>Fanible is exclusively designed for sports communities. Users may publish content related to:</p>
          <Bullets items={[
            "Cricket", "Football", "Basketball", "Tennis", "Formula 1", "Hockey", "Badminton",
            "Kabaddi", "Volleyball", "Athletics", "Esports", "Any other legitimate sport recognized by Fanible",
          ]} />
          <p className="text-white font-semibold">Content unrelated to sports is prohibited.</p>
          <p>Fanible reserves the right to remove non-sports content without prior notice.</p>
        </Section>

        <Section num="5" title="User Content">
          <p>Users may create and publish:</p>
          <Bullets items={["Text posts", "Sports news", "Sports blogs", "Sports articles", "Sports-related images"]} />
          <p>Users may delete their own posts. Users may <span className="text-white font-semibold">not</span> edit published posts after submission.</p>
          <p>Fanible may preserve deleted content where required by law or for legitimate business purposes.</p>
        </Section>

        <Section num="6" title="Community Standards">
          <p>Users shall not upload, publish, distribute, promote, or otherwise make available any content that:</p>
          <Bullets items={[
            "Promotes hatred or discrimination",
            "Contains pornography or sexually explicit material",
            "Infringes copyrights or intellectual property rights",
            "Encourages violence or criminal activities",
            "Contains spam or deceptive information",
            "Impersonates another individual or organization",
            "Promotes gambling or betting services",
            "Violates applicable laws",
            "Contains malware or harmful software",
            "Harasses, bullies, threatens, or intimidates others",
            "Spreads misinformation intended to cause harm",
            "Contains content unrelated to sports",
          ]} />
          <p>Fanible may remove any content that violates these standards without prior notice.</p>
        </Section>

        <Section num="7" title="Creator Program">
          <p>Certain users may qualify as Creators. Eligibility for the Creator Program is determined solely by Fanible.</p>
          <p>Creators may receive monetary support through the Shoutout feature.</p>
          <p>
            Participation in the Creator Program does not establish any employment, partnership, agency, or
            joint venture relationship between Fanible and the Creator.
          </p>
          <p>Creators remain solely responsible for complying with applicable tax and legal obligations arising from their earnings.</p>
        </Section>

        <Section num="8" title="Shoutout Payments">
          <p>Users may voluntarily support eligible Creators through paid Shoutouts.</p>
          <Bullets items={[
            "All payments are processed through authorized third-party payment providers",
            "Fanible does not guarantee that any Creator will receive a specific amount of support",
            "Users are solely responsible for confirming the recipient before completing payment",
            "Once a payment has been successfully transferred to a Creator, the transaction is considered final",
          ]} />
          <p>
            Shoutout payments are voluntary and should not be interpreted as purchasing ownership, influence,
            or control over any Creator or their content.
          </p>
        </Section>

        <Section num="9" title="Platform Fees">
          <InfoBox>
            <p className="text-white font-semibold">
              Fanible deducts a <span className="text-[#3B9EFF]">25% platform service fee</span> from eligible
              Creator earnings before processing withdrawals.
            </p>
            <p>Platform fees may be modified from time to time.</p>
            <p>Any updated fee structure will be communicated through the Platform before taking effect.</p>
          </InfoBox>
        </Section>

        <Section num="10" title="Creator Withdrawals">
          <p>Eligible Creators may withdraw available earnings subject to the following conditions:</p>
          <InfoBox>
            <div className="space-y-2 text-sm">
              <p><span className="text-[#A1A1AA]">Minimum withdrawal amount: </span><span className="text-white font-semibold">₹100</span></p>
              <p><span className="text-[#A1A1AA]">Withdrawal methods: </span><span className="text-white">UPI · Bank Transfer</span></p>
              <p><span className="text-[#A1A1AA]">Estimated processing time: </span><span className="text-white">Within 24 hours after successful verification</span></p>
            </div>
          </InfoBox>
          <p>Fanible reserves the right to request additional verification before processing withdrawals.</p>
          <p>Withdrawals may be delayed or declined where fraud, suspicious activity, legal restrictions, or policy violations are detected.</p>
        </Section>

        <Section num="11" title="Refund Policy">
          <p>Payments made through the Shoutout feature are voluntary.</p>
          <p className="text-white font-semibold">
            Once a payment has been successfully completed and transferred to the intended Creator,{" "}
            <span className="text-[#f59e0b]">no refunds will be issued</span>.
          </p>
          <p>Users are responsible for verifying payment details before completing any transaction.</p>
          <p>Refunds, if any, shall be governed by applicable law and the policies of the payment service provider where legally required.</p>
        </Section>

        <Section num="12" title="Fanible E-Shop">
          <p>The Platform may provide access to sports-related merchandise through the Fanible e-shop.</p>
          <p>Availability of products, pricing, delivery timelines, warranties, and returns may vary.</p>
          <p>Additional purchase terms may apply to merchandise sold through the e-shop.</p>
          <p>Users agree to comply with any applicable product-specific policies published by Fanible.</p>
        </Section>

        <Section num="13" title="Intellectual Property">
          <p>
            All intellectual property rights in the Platform, including but not limited to its design, user
            interface, software, logos, trademarks, graphics, icons, databases, source code, text, audio, and
            other content created by Fanible, are owned by or licensed to Fanible Private Limited and are
            protected under applicable intellectual property laws.
          </p>
          <p>Nothing contained in these Terms grants users ownership of any intellectual property belonging to Fanible.</p>
          <p>Users may not:</p>
          <Bullets items={[
            "Copy, reproduce, distribute, modify, reverse engineer, or create derivative works from the Platform",
            "Use Fanible's trademarks, branding, or logos without prior written permission",
            "Attempt to gain unauthorized access to any part of the Platform or its systems",
          ]} />
        </Section>

        <Section num="14" title="Ownership of User Content">
          <p>Users retain ownership of the original content they create and publish on Fanible.</p>
          <p>
            By posting content on the Platform, you grant Fanible a worldwide, non-exclusive, royalty-free,
            transferable, sublicensable license to host, store, reproduce, display, distribute, adapt,
            promote, and make your content available solely for operating, improving, promoting, and
            providing the Platform and its services.
          </p>
          <p>
            This license continues for as long as your content remains available on the Platform or as
            otherwise required for legal, operational, backup, security, dispute resolution, or archival
            purposes.
          </p>
          <p>You represent and warrant that:</p>
          <Bullets items={[
            "You own or have sufficient rights to the content you upload",
            "Your content does not violate any law or third-party rights",
            "You have obtained all necessary permissions where required",
          ]} />
        </Section>

        <Section num="15" title="Content Moderation">
          <p>Fanible is committed to maintaining a respectful and sports-focused community.</p>
          <p>Fanible reserves the right, at its sole discretion, to:</p>
          <Bullets items={[
            "Review user-generated content",
            "Remove any content that violates these Terms",
            "Restrict the visibility of content",
            "Suspend or terminate user accounts",
            "Report illegal activities to appropriate authorities where required by law",
          ]} />
          <p>Fanible is under no obligation to publish or continue hosting any particular content.</p>
        </Section>

        <Section num="16" title="Three-Strike Enforcement Policy">
          <p>To promote a safe community, Fanible may apply the following enforcement process for policy violations:</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
            {[
              { strike: "First Violation", action: "Warning and/or temporary suspension" },
              { strike: "Second Violation", action: "Longer temporary suspension" },
              { strike: "Third Violation", action: "Permanent account suspension or termination" },
            ].map(({ strike, action }) => (
              <div key={strike} className="bg-white/[0.03] border border-white/8 rounded-xl p-4">
                <p className="text-[#3B9EFF] text-[10px] font-black uppercase tracking-widest mb-1.5">{strike}</p>
                <p className="text-white text-xs leading-relaxed">{action}</p>
              </div>
            ))}
          </div>
          <p>
            Fanible reserves the right to permanently suspend any account immediately without prior warning
            in cases involving serious violations, including but not limited to fraud, impersonation, illegal
            activities, child exploitation, threats of violence, copyright abuse, payment fraud, or any
            activity that poses significant risk to users or the Platform.
          </p>
        </Section>

        <Section num="17" title="User Responsibilities">
          <p>You agree to use Fanible responsibly and lawfully. You shall not:</p>
          <Bullets items={[
            "Attempt to disrupt the Platform",
            "Circumvent security measures",
            "Create fake accounts",
            "Manipulate votes or engagement using automated tools",
            "Engage in fraudulent payment activities",
            "Sell or transfer your account",
            "Use bots, scripts, or automated software without authorization",
            "Upload malicious software or viruses",
          ]} />
          <p>You remain solely responsible for all activities conducted through your account.</p>
        </Section>

        <Section num="18" title="Third-Party Services">
          <p>Fanible may integrate with third-party services including, but not limited to:</p>
          <Bullets items={[
            "Google Sign-In",
            "WhatsApp authentication services",
            "Razorpay payment services",
            "Cloud hosting providers",
            "Analytics and notification services",
          ]} />
          <p>Your use of such services may also be governed by the respective third-party terms and privacy policies.</p>
          <p>Fanible is not responsible for the services, availability, or actions of third-party providers.</p>
        </Section>

        <Section num="19" title="Privacy">
          <p>
            Your use of the Platform is also governed by{" "}
            <a href="/privacy" className="text-[#3B9EFF] hover:underline">Fanible's Privacy Policy</a>,
            which explains how personal information is collected, used, stored, and protected.
          </p>
          <p>
            By using Fanible, you consent to the collection and processing of your information as described
            in the Privacy Policy.
          </p>
        </Section>

        <Section num="20" title="Account Deletion">
          <p>Users may request deletion of their account by contacting:</p>
          <p>
            Email: <a href="mailto:hello@fanible.com" className="text-[#3B9EFF] hover:underline">hello@fanible.com</a>
          </p>
          <p>
            Web: <a href="/delete-account" className="text-[#3B9EFF] hover:underline font-medium">https://www.fanible.com/delete-account</a>
          </p>
          <p>Fanible will verify the request and initiate account deletion within 24 hours of successful verification.</p>
          <p>
            Certain information may be retained where required by applicable law or for fraud prevention,
            tax, accounting, dispute resolution, security, or other legitimate business purposes.
          </p>
          <p>
            Deletion of an account may result in permanent loss of access to certain content, earnings, or
            services associated with the account, except where otherwise required by law.
          </p>
        </Section>

        <Section num="21" title="Disclaimer of Warranties">
          <p>The Platform is provided on an <span className="text-white font-semibold">"AS IS"</span> and <span className="text-white font-semibold">"AS AVAILABLE"</span> basis.</p>
          <p>
            To the maximum extent permitted by applicable law, Fanible disclaims all warranties, whether
            express, implied, statutory, or otherwise, including warranties of merchantability, fitness for
            a particular purpose, non-infringement, uninterrupted availability, accuracy, or reliability.
          </p>
          <p>Fanible does not guarantee:</p>
          <Bullets items={[
            "Uninterrupted access to the Platform",
            "Error-free operation",
            "Continuous availability",
            "Complete accuracy of user-generated content",
            "Compatibility with all devices",
          ]} />
        </Section>

        <Section num="22" title="Limitation of Liability">
          <p>
            To the fullest extent permitted by applicable law, Fanible Private Limited, its directors,
            officers, employees, affiliates, partners, and licensors shall not be liable for any indirect,
            incidental, special, consequential, exemplary, or punitive damages arising out of or related to
            your use of the Platform.
          </p>
          <p>This includes, but is not limited to:</p>
          <Bullets items={[
            "Loss of profits",
            "Loss of business opportunities",
            "Data loss",
            "Interruption of services",
            "Payment disputes between users and creators",
            "Unauthorized account access resulting from your failure to secure your credentials",
            "Actions of other users",
          ]} />
          <p>Nothing in these Terms limits liability where such limitation is prohibited by applicable law.</p>
        </Section>

        <Section num="23" title="Indemnification">
          <p>
            You agree to indemnify, defend, and hold harmless Fanible Private Limited, its directors,
            employees, affiliates, agents, licensors, and partners from and against any claims, damages,
            liabilities, losses, costs, expenses, or legal fees arising from:
          </p>
          <Bullets items={[
            "Your violation of these Terms",
            "Your misuse of the Platform",
            "Your infringement of any third-party rights",
            "Your violation of applicable laws",
            "Your user-generated content",
          ]} />
        </Section>

        <Section num="24" title="Suspension and Termination">
          <p>Fanible may suspend, restrict, or terminate your account at any time if:</p>
          <Bullets items={[
            "You violate these Terms",
            "You engage in fraudulent activities",
            "You abuse the Platform",
            "You repeatedly violate community standards",
            "Your activities threaten the safety, security, or integrity of the Platform or its users",
          ]} />
          <p>Termination does not affect any legal rights or obligations that accrued prior to termination.</p>
        </Section>

        <Section num="25" title="Changes to These Terms">
          <p>Fanible may modify these Terms from time to time.</p>
          <p>Material changes will become effective upon publication on the Platform or website unless otherwise stated.</p>
          <p>Your continued use of Fanible after updated Terms become effective constitutes acceptance of the revised Terms.</p>
        </Section>

        <Section num="26" title="Governing Law and Jurisdiction">
          <p>
            These Terms shall be governed by and interpreted in accordance with the laws of India, without
            regard to conflict of law principles.
          </p>
          <p>
            Any disputes arising out of or relating to these Terms or your use of the Platform shall be
            subject to the exclusive jurisdiction of the competent courts located in{" "}
            <span className="text-white font-semibold">Samastipur, Bihar, India</span>.
          </p>
        </Section>

        <Section num="27" title="Severability">
          <p>
            If any provision of these Terms is found to be invalid, illegal, or unenforceable by a court of
            competent jurisdiction, the remaining provisions shall remain in full force and effect.
          </p>
        </Section>

        <Section num="28" title="Entire Agreement">
          <p>
            These Terms, together with the Privacy Policy and any additional policies published by Fanible,
            constitute the entire agreement between you and Fanible regarding your use of the Platform and
            supersede all prior agreements, understandings, or communications relating to the subject matter
            herein.
          </p>
        </Section>

        <Section num="29" title="Contact Information">
          <p>If you have any questions, concerns, or requests regarding these Terms, you may contact us at:</p>
          <div className="bg-white/[0.03] border border-white/8 rounded-2xl p-6 mt-2 space-y-3">
            <p className="text-white font-semibold">Fanible Private Limited</p>
            <div className="space-y-1.5 text-sm">
              <p><span className="text-[#A1A1AA]">Registered Office: </span><span className="text-white">Samastipur, Bihar, India</span></p>
              <p><span className="text-[#A1A1AA]">Website: </span><a href="https://www.fanible.com" className="text-[#3B9EFF] hover:underline">www.fanible.com</a></p>
              <p><span className="text-[#A1A1AA]">Support Email: </span><a href="mailto:support@fanible.com" className="text-[#3B9EFF] hover:underline">support@fanible.com</a></p>
              <p><span className="text-[#A1A1AA]">General & Legal Inquiries: </span><a href="mailto:hello@fanible.com" className="text-[#3B9EFF] hover:underline">hello@fanible.com</a></p>
            </div>
          </div>
          <p className="text-white font-medium pt-2">
            By creating an account or using Fanible, you acknowledge that you have read, understood, and
            agree to be bound by these Terms and Conditions.
          </p>
        </Section>

        {/* Footer */}
        <div className="pt-6 border-t border-white/8 flex items-center justify-between">
          <a href="/" className="text-sm text-[#A1A1AA] hover:text-[#3B9EFF] transition-colors font-inter">← Back to Fanible</a>
          <p className="text-[#A1A1AA] text-xs font-inter">© 2026 Fanible Private Limited. All rights reserved.</p>
          <a href="/privacy" className="text-sm text-[#A1A1AA] hover:text-white transition-colors font-inter">← Privacy Policy</a>
        </div>

      </div>
    </div>
  );
}
